export type VisitorDerivedStatus =
  | 'onsite'
  | 'in_app'
  | 'web_session'
  | 'app_session'
  | 'offline';

export interface ClickEvent {
  x: number;
  y: number;
  timestamp: string;
  element: string;
  element_name?: string;
  element_text?: string;
  parent_window_url: string;
}

export interface PageView {
  page: string;
  timestamp: string;
}

export interface VisitorPresence {
  visitor_id: string;
  agent_id: string;
  organization_id: string;
  current_page?: string | null;
  status: 'active' | 'inactive';
  derived_status?: VisitorDerivedStatus;
  status_label?: string;
  last_seen: string;
  conversation_id?: string;
  session_active?: boolean;
  click_events?: ClickEvent[];
  page_view_history?: PageView[];
  ip_address?: string;
  city?: string;
  region?: string;
  country?: string;
  conversation_count?: number;
  latest_conversation_id?: string;
  is_warm?: boolean;
  warm_reasons?: string[];
  human_agent?: {
    id: string;
    name?: string;
    email?: string;
  };
  online?: boolean;
  in_app?: boolean;
  presence_surface?: 'web' | 'app';
  last_seen_at?: string | null;
  first_seen_at?: string | null;
  identity?: any;
}

export type LiveBoardLane = 'contacts' | 'warm' | 'new';

export type LiveBoardLanes = Record<LiveBoardLane, string[]>;

const LIVE_FIELDS: Array<keyof VisitorPresence> = [
  'online',
  'status',
  'derived_status',
  'status_label',
  'in_app',
  'presence_surface',
  'current_page',
  'last_seen',
  'last_seen_at',
  'click_events',
  'page_view_history',
  'ip_address',
  'conversation_id',
  'latest_conversation_id',
  'conversation_count',
  'session_active',
  'human_agent',
];

/**
 * Snapshot data owns persisted/enriched fields. If a websocket mutation landed
 * after the request began, its live fields win so an older response cannot
 * rewind presence, activity, or session state.
 */
export function mergeSnapshotVisitor(
  current: VisitorPresence | undefined,
  snapshot: VisitorPresence,
  preserveLiveFields: boolean,
): VisitorPresence {
  if (!current) return snapshot;
  const merged = { ...current, ...snapshot };
  if (preserveLiveFields) {
    LIVE_FIELDS.forEach((field) => {
      if (current[field] !== undefined) {
        (merged as any)[field] = current[field];
      }
    });
  }
  return merged;
}

export function deriveVisitorStatus(visitor: VisitorPresence): VisitorDerivedStatus {
  const online = Boolean(visitor.online || visitor.status === 'active' || visitor.session_active);
  if (!online) return 'offline';
  const inApp = Boolean(visitor.in_app || visitor.presence_surface === 'app');
  if (visitor.session_active) return inApp ? 'app_session' : 'web_session';
  return inApp ? 'in_app' : 'onsite';
}

export function classifyLiveBoardLane(
  visitor: VisitorPresence,
  now = new Date(),
): LiveBoardLane {
  if (visitor.identity?.summary?.status === 'contact') return 'contacts';
  if (typeof visitor.is_warm === 'boolean') return visitor.is_warm ? 'warm' : 'new';
  if ((visitor.conversation_count || 0) > 0) return 'warm';

  const activityTimestamps = [
    ...(visitor.click_events || []).map((event) => event.timestamp),
    ...(visitor.page_view_history || []).map((view) => view.timestamp),
  ]
    .map((timestamp) => new Date(timestamp).getTime())
    .filter(Number.isFinite);
  const firstActivity = activityTimestamps.length ? Math.min(...activityTimestamps) : null;
  if (firstActivity !== null && now.getTime() - firstActivity >= 7 * 24 * 60 * 60 * 1000) {
    return 'warm';
  }
  if ((visitor.page_view_history?.length || 0) >= 5) return 'warm';
  return 'new';
}

/**
 * Creates a board snapshot. Consumers keep this result unchanged while visitor
 * records continue receiving live content updates, then rebuild it when the
 * user applies pending presentation changes.
 */
export function buildLiveBoardLanes(
  order: string[],
  visitors: ReadonlyMap<string, VisitorPresence>,
  now = new Date(),
): LiveBoardLanes {
  const lanes: LiveBoardLanes = { contacts: [], warm: [], new: [] };
  order.forEach((id) => {
    const visitor = visitors.get(id);
    if (!visitor || deriveVisitorStatus(visitor) === 'offline') return;
    lanes[classifyLiveBoardLane(visitor, now)].push(id);
  });
  return lanes;
}

export function applyPendingVisitorOrder(
  desiredOrder: string[],
  availableIds: ReadonlySet<string>,
): string[] {
  const seen = new Set<string>();
  return desiredOrder.filter((id) => {
    if (!availableIds.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function changedBoardVisitorIds(
  current: LiveBoardLanes,
  next: LiveBoardLanes,
): Set<string> {
  const placement = (lanes: LiveBoardLanes) => {
    const result = new Map<string, LiveBoardLane>();
    (Object.keys(lanes) as LiveBoardLane[]).forEach((lane) => {
      lanes[lane].forEach((id) => result.set(id, lane));
    });
    return result;
  };
  const before = placement(current);
  const after = placement(next);
  const ids = new Set([...before.keys(), ...after.keys()]);
  return new Set([...ids].filter((id) => before.get(id) !== after.get(id)));
}

export function appendBounded(set: Set<string>, id: string, limit = 200): Set<string> {
  const next = new Set(set);
  next.delete(id);
  next.add(id);
  while (next.size > limit) {
    const oldest = next.values().next().value;
    if (oldest === undefined) break;
    next.delete(oldest);
  }
  return next;
}

export function dedupeActivity<T>(
  items: T[],
  key: (item: T) => string,
  limit: number,
): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const value = key(item);
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  }).slice(-limit);
}
